import { debounceTime } from 'rxjs/operators';
import { Subscription, Subject } from 'rxjs';
import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TraceService } from './../../core/services/trace.service';
import { SyncronizationService } from './../../core/services/synchronization.service';
import { ConfirmDialogComponent } from '../../../shared/components/confirm-dialog/confirm-dialog.component';
import { ViewerActions } from './../../core/reducers/viewer.actions';
import { TraceManagementStateService } from 'app/trace-management/trace-manageent.state.service';
import { BzgModalService } from 'bz-genome-angular';
import { DeleteTracingAlertComponent } from './trace-viewer-delete.component';
import { DeleteConfirmationComponent } from '../../../shared/components/delete-confirmation/delete-confirmation.component';
import { ModalViewerDetailComponent } from '../modal-detail/modal-viewer-detail.component';
import { DateUtil } from "../../../core/utils/DateUtil";
import { TranslateService } from '@ngx-translate/core';


@Component({
  selector: 'biz-trace-viewer',
  templateUrl: './trace-viewer.component.html',
  styleUrls: ['./trace-viewer.component.scss']
})
export class TraceViewerComponent implements OnInit {

  entries: Array<IBATraceFile> = [];
  contentEntries: Array<IBATraceDetail>;
  showDetail = false;
  headerName: string;
  continuationToken: string;
  continuationTokenDetail: string;
  showLoading = false;
  loadingMessage = '';
  hasMoreResults: boolean;
  isShowingNoMoreItems = false;
  traceFilter: ITraceFilter;
  selectAll: boolean;
  fileNameFilter: string;
  isSearching: boolean = false;
  state = { dateMin: new Date(), dateMax: new Date(), category: '', fileNameFilter: '' };

  public cellSpacingMode = 'normal';
  public selectedCategory: string;
  public categories: Array<any>
  public selectedEntries: Array<IBATraceFile> = [];

  private noLoadMoreResults = false;
  private isLoadingMoreResults = false;
  private lastEntry: IBATraceFile;
  private subscription: any;
  private gettingIntoTracing: any;
  private eventSubscription: any;
  private fileNameChangeSubject = new Subject();
  private fileNameChangeSubscription: Subscription;
  @ViewChild('confirmDialog', { static: false }) confirmDialog: ConfirmDialogComponent;

  constructor(
    private _traceService: TraceService,
    private _store: Store<any>,
    private route: ActivatedRoute,
    private _router: Router,
    private _viewerActions: ViewerActions,
    private _syncronize: SyncronizationService,
    private _modalService: BzgModalService,
    private _traceManagementStateService: TraceManagementStateService,
    private _translateService: TranslateService
  ) {
    this.initializeTraceFilter();
    this.state = _traceManagementStateService.getState();
    this._traceService.getCategories()
      .subscribe((categories) => {
        this.categories = categories;
        this.setCategory('Uncategorized');
      });
  }

  ngOnInit() {
    this.selectedCategory = '';
    this.state.dateMin = new Date();
    this.state.dateMax = new Date();
    this.state.category = '';
    this.state.fileNameFilter = '';
    this.getCategories();
    this.initializeTraceFilter();
    this.loadMoreTraceFiles();
    this.eventSubscription = this._syncronize.EventReloadContent.subscribe(this.loadMoreTraceFiles.bind(this));

    this.fileNameChangeSubscription = this.fileNameChangeSubject.pipe(debounceTime(1000)).subscribe(() => {
      this.state.fileNameFilter = this.fileNameFilter;
      this._traceManagementStateService.setState(this.state);
      this.loadMoreTraceFiles(true);
    });
  }

  getCategories() {
    this._traceService.getCategories()
      .subscribe((categories) => {
        this.categories = categories;
        this.setCategory('Uncategorized');
      });
  }

  viewdetail(entry) {
    if (!entry.item) {
      entry.item = this.lastEntry;
    }
    this.lastEntry = entry.item;
    this.showViewerDialog(entry.item);
  }

  change(event: any) {
    this.fileNameFilter = event.target.value.toLowerCase().replace(/^\s+|\s+$/gm, '');
    this.fileNameChangeSubject.next();
  }

  refresh() {
    this.entries = [];
    this.hasMoreResults = false;
    this.continuationToken = '';
    this.refreshDatesRange();
    this.loadMoreTraceFiles();
  }

  refreshDatesRange() {
    if (this.state.dateMin) {
      this.traceFilter.startDate = DateUtil.getFirstDayTime(this.state.dateMin);
    }
    if (this.state.dateMax) {
      this.traceFilter.endDate = DateUtil.getLastDayTime(this.state.dateMax);
    }
  }

  private createFileContent(entries: Array<IBATraceDetail>, fileName: string) {
    const fileContent: Array<Array<string>> = [];

    fileContent.push([fileName]);
    entries.forEach((value: IBATraceDetail) => {
      fileContent.push([value.MessageLog]);
    });

    return fileContent;
  }

  public setCategory(category) {
    if (category !== this.selectedCategory) {
      this.selectedCategory = category;
      this.state.category = this.selectedCategory;
      this._traceManagementStateService.setState(this.state);
      this.continuationToken = '';
      this.loadMoreTraceFiles();
    }

  }

  public changeEntryDelete(index, value) {
    this.entries = [...this.entries.slice(0, index),
    { ...this.entries[index], Delete: !value },
    ...this.entries.slice(index + 1),]
  }

  public filterFileTraces(dateFilter: any) {
    if (dateFilter.start && dateFilter.end) {
      this.entries = [];
      // Update state
      this.state = {
        dateMin: dateFilter.start,
        dateMax: dateFilter.end,
        category: this.selectedCategory,
        fileNameFilter: this.fileNameFilter
      }
      this._traceManagementStateService.setState(this.state);

      this.hasMoreResults = false;
      this.continuationToken = '';
      this.refreshDatesRange();
      this.loadMoreTraceFiles();
    }
  }

  loadMoreTraceFiles(changeFromImput = false): any {
    if (!this.isLoadingMoreResults && this.categories) {
      this.showLoading = true;
      this.loadingMessage = '';
    }
    this.selectAll = false;
    this.traceFilter.filterProperties.continuationToken = changeFromImput ? null : this.continuationToken;
    this.traceFilter.name = this.state.fileNameFilter;
    this.traceFilter.category = this.state.category ? this.state.category : 'Uncategorized';
    this.traceFilter.startDate = DateUtil.setFirstHourDay(this.state.dateMin);
    this.traceFilter.endDate = DateUtil.setLastHourDay(this.state.dateMax);
    if (!this.isSearching && this.categories) {
      this.isSearching = true;
      this._traceService.getFileNames(this.traceFilter).subscribe(entries => {
        this.isSearching = false;

        if (!this.isLoadingMoreResults) {
          this.entries = entries.Results;
          this.noLoadMoreResults = false;
        }

        else {
          $('.bz-table-adv__row--load-more').remove();
          this.isLoadingMoreResults = false;
          const newEntries = this.entries.concat(entries.Results);
          this.entries = newEntries;
        }


        this.hasMoreResults = entries.HasMoreResults;
        this.continuationToken = entries.ContinuationToken;
        // this._store.dispatch(this._viewerActions.Add(this.entries));
        this.showLoading = false;
        this.attachScrollEvent();
      });
    }
  }

  public setSelectedEntries(entries) {
    this.selectedEntries = entries;
  }

  private attachScrollEvent() {
    setTimeout(() => {
      $('bzg-table .bz-table-adv__body').off('scroll');
      $('bzg-table .bz-table-adv__body').on('scroll', (event) => {
        const tableBody = $(event.currentTarget);
        if (tableBody.scrollTop() +
          tableBody.innerHeight() >=
          tableBody[0].scrollHeight - 10) {
          if (!this.showLoading && !this.isLoadingMoreResults && !this.noLoadMoreResults) {

            const content = $('<p></p>');

            this._translateService.get(`TRACE.${this.hasMoreResults ? 'LOAD_MORE_RESULTS' : 'NO_MORE_ITEMS'}`).subscribe((message: string) => {
              content.text(message);
            });

            const row = $('<div></div>').addClass('bz-table-adv__row').append(content);
            const rowWrapper = $('<div></div>').addClass('bz-table-adv__row-wrapper')
              .addClass('bz-table-adv__row--load-more').append(row);
            tableBody.append(rowWrapper);

            if (this.hasMoreResults) {
              this.isLoadingMoreResults = true;
              this.loadMoreTraceFiles();
            }
            else {
              this.noLoadMoreResults = true;
            }
          }
        }
      });
    }, 0);
  }

  private initializeTraceFilter() {
    this.entries = [];
    this.continuationToken = '';
    this.hasMoreResults = false;
    this.fileNameFilter = this.state.fileNameFilter;
    this.selectedCategory = this.state.category;
    this.traceFilter = {
      filterProperties: {
        pageSize: 20,
        continuationToken: this.continuationToken,
        sort: 'Asc'
      } as IFilterProperties,
      name: this.state.fileNameFilter,
      category: this.state.category,
      startDate: DateUtil.getFirstDayTime(this.state.dateMin),
      endDate: DateUtil.getLastDayTime(this.state.dateMax)
    };

  }

  public ngOnDestroy() {
    this.eventSubscription.unsubscribe();

  }

  public downloadFile() {
    this.showLoading = true;
    this.loadingMessage = `Downloading ${this.selectedEntries[0].Name}`;

    this._traceService.downloadTraceFile(this.selectedEntries[0])
      .subscribe(() => {
        this.showLoading = false;
      });
  }

  private selectAllTraceFiles() {
    if (this.entries) {
      this.selectAll = !this.selectAll;
      this.entries.forEach(e => e.Delete = this.selectAll);
    }
  }

  public numFilesToDelete() {
    const filesToDelete = this.entries ? this.entries.filter((e) => e.Delete === true) : [];
    return filesToDelete.length;
  }

  public showConfirmDialog() {
    const modal = this._modalService.open(DeleteConfirmationComponent, {
      closeOnBackdropClick: false,
      closeOnRouterNavigation: true,
      data: {
        title: 'TRACE.DELETE_TITLE',
        message: 'TRACE.DELETE_CONFIRMATION'
      }
    });
    modal.afterClosed().subscribe((result: string) => {
      if (result === 'delete') {
        this.removeTraceFile();
      }
    })
  }

  public showViewerDialog(entry: IBATraceFile) {
    this._modalService.open(ModalViewerDetailComponent, {
      closeOnBackdropClick: false,
      data: {
        entry
      }, width: '70%',
      maxWidth: 'none',
      maxHeight: '600px',
      closeOnRouterNavigation: true
    });
  }

  public removeTraceFile() {
    this.showLoading = true;
    this.loadingMessage = 'Removing Trace File...'
    // const traceFilesToDelete = this.entries.filter((e) => e.Delete === true);
    const object = this.selectedEntries.map((e) => ({ partitionKey: e.PartitionKey, rowKey: e.RowKey, key: e.Name }));
    this._traceService.removeTraceFiles({ items: object }).subscribe(() => {
      this.showLoading = false;
      this.hasMoreResults = false;
      this.continuationToken = '';
      this.entries = [];
      this.loadMoreTraceFiles();
    });
  }
}



